import { Box } from "@mui/material"
import { ReactElement } from "react"

import ExpandedVerticalNavOptions from "./navBarVerticalExpandedOptions"
import { NAV_ICON_SX } from "./navBarWrapper"
import NavButtonVerticalCollapsed from "./navButtons/navButtonVerticalCollapsed"
import {
    NAVBAR_COLLAPSED_WIDTH_PX,
    NAVBAR_EXPANDED_WIDTH_PX,
} from "../../../constants/generalConstants"
import { useGetState } from "../../../hooks/useGetState"
import { getNavBarExpand } from "../../../redux/preferencesSlice"
import { navOptionsByGroup, NavOptionType } from "../navOptions"

type NavBarVerticalProps = {
    groups: string[]
    children: ReactElement
}

/**
 * Returns navigation bar in vertical, upright mode. Includes both expanded
 * and collapsed variants of the component. Wraps the content box for the
 * application, via the `children` component
 *
 * @example
 * <NavBarVertical>
 *     <Content />
 * </NavBarVertical>
 */
const NavBarVertical = ({
    groups,
    children,
}: NavBarVerticalProps): ReactElement => {
    const navbarExpand = useGetState(getNavBarExpand)
    return (
        <Box
            display="flex"
            height="100%"
            alignItems="stretch"
            flexDirection="row"
        >
            <Box
                width={
                    navbarExpand
                        ? NAVBAR_EXPANDED_WIDTH_PX
                        : NAVBAR_COLLAPSED_WIDTH_PX
                }
                sx={{
                    backgroundColor: "background.frameBackground",
                    backgroundImage: "none",
                    transition: "cubic-bezier(0.4, 0, 0.2, 1) 0.8s;",
                }}
                paddingX={2}
                overflow="scroll"
            >
                {navbarExpand ? (
                    <ExpandedVerticalNavOptions
                        groups={navOptionsByGroup(groups).groupOrder}
                        options={navOptionsByGroup(groups).options}
                    />
                ) : (
                    navOptionsByGroup(groups).options.map(
                        (option: NavOptionType) => (
                            <NavButtonVerticalCollapsed
                                key={`NavVerticalCollapsed${option.group}${option.name}`}
                                title={option.name}
                                route={option.route}
                                external={option?.external}
                                icon={
                                    <option.icon
                                        sx={{
                                            ...NAV_ICON_SX,
                                            mr: 0,
                                        }}
                                    />
                                }
                            />
                        )
                    )
                )}
            </Box>
            {children}
        </Box>
    )
}

export default NavBarVertical
